import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Tag,
  Flag,
  FileText,
  BarChart,
  Users,
  Clipboard,
  CalendarClock,
  ClipboardCheck,
  Hospital,
  LayoutGrid,
  CircleDollarSign,
  LockKeyholeOpen,
  Scroll, 
  ListTree,
  Eye,
} from 'lucide-react';
import './Menu.css';

const Menu = ({
  isOpen,
  labelList = [],
  labelFilter = [],
  statusFilter = [],
  boardStatusList = [],
  queryParams,
  setQueryParams,
  user, 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.roles?.is_admin;
  const isLeadership = user?.roles?.is_leadership; 
  
  const isVisitsPage = location.pathname === '/visits';

  const [showLabelFlyout, setShowLabelFlyout] = useState(false);
  const flyoutRef = useRef(null);
  const labelButtonRef = useRef(null);
  
  const [showStatusFlyout, setShowStatusFlyout] = useState(false);
  const statusButtonRef = useRef(null);
  const statusFlyoutRef = useRef(null);

  const [showBoardFlyout, setShowBoardFlyout] = useState(false);
  const boardButtonRef = useRef(null);
  const boardFlyoutRef = useRef(null);
  
  const card_status_list = ["Hold", "New", "New Day", "Complete"];

  useEffect(() => {
    const handleClickOutside = (event) => {
      const refs = [
        flyoutRef,
        labelButtonRef,
        statusFlyoutRef,
        statusButtonRef,
        boardFlyoutRef,
        boardButtonRef,
      ];
  
      const clickedInsideSomeRef = refs.some(
        ref => ref.current && ref.current.contains(event.target)
      );
  
      if (!clickedInsideSomeRef) {
        setShowLabelFlyout(false);
        setShowStatusFlyout(false);
        setShowBoardFlyout(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const label = (text) =>
    isOpen ? <span className="label">{text}</span> : null;


  const toggleParam = (key) => {
    if (!setQueryParams) return;
    setQueryParams((prev) => ({
      ...prev,
      [key]: !prev[key],
      page: 1,
    }));
  };

  const selectAll = () => {
    if (!setQueryParams) return;
    const allNames = labelList.map((l) => l.label_name);
    setQueryParams((prev) => ({
      ...prev,
      labelFilter: allNames,
      page: 1,
    }));
  };

  const deselectAll = () => {
    if (!setQueryParams) return;
    setQueryParams((prev) => ({
      ...prev,
      labelFilter: [],
      page: 1,
    }));
  };

  const toggleLabelFlyout = () => {
    setShowLabelFlyout(prev => !prev);
    setShowStatusFlyout(false);
    setShowBoardFlyout(false);
  };
  
  const toggleStatusFlyout = () => {
    setShowStatusFlyout(prev => !prev);
    setShowLabelFlyout(false);
    setShowBoardFlyout(false);
  };
  
  const toggleBoardFlyout = () => {
    setShowBoardFlyout(prev => !prev);
    setShowLabelFlyout(false);
    setShowStatusFlyout(false);
  };

  return (
    <nav className={`menu ${isOpen ? 'open' : 'collapsed'}`}>
      <div className="menu-section-wrapper">

        {isVisitsPage && (
          <>
            {isOpen && <div className="menu-section-header">Filters</div>}
            <ul className="menu-section">
              <li
                title="Labels"               
                className={`has-flyout ${
                  showLabelFlyout || labelFilter.length > 0 ? 'selected' : ''
                }`}
                onClick={toggleLabelFlyout}
                ref={labelButtonRef}
              >
                <Tag size={20} className="menu-icon" />
                {label(
                  `Labels${labelFilter.length ? ` (${labelFilter.length})` : ''}`
                )}
              </li>

              <li
                title="HomeSoon"
                className={`filter-item ${
                  queryParams?.expiringOnly ? 'selected' : ''
                }`}
                onClick={() => toggleParam('expiringOnly')}
              >
                <Home size={20} className="menu-icon" />
                {label('TGH')}
              </li>

              <li
                title="Alerts"
                className={`filter-item ${
                  queryParams?.hideClean ? 'selected' : ''
                }`}
                onClick={() => toggleParam('hideClean')}
              >
                <Flag size={20} className="menu-icon" />
                {label('Issues')}
              </li>

              <li
                title="Open30"
                className={`filter-item ${
                  queryParams?.open30plus ? 'selected' : ''
                }`}
                onClick={() => toggleParam('open30plus')}
              >
                <CalendarClock size={20} className="menu-icon" />
                {label('Open 30+')}
              </li>

              <li
                title="Status"
                className={`has-flyout ${
                  showStatusFlyout || queryParams?.statusFilter?.length ? 'selected' : ''
                }`}
                onClick={toggleStatusFlyout}
                ref={statusButtonRef}
              >
                <Clipboard size={20} className="menu-icon" />
                {label(
                  `Status${
                    queryParams?.statusFilter?.length
                      ? ` (${queryParams.statusFilter.length})`
                      : ''
                  }`
                )}
              </li>
              <li
                title="Board"
                className={`has-flyout ${
                  showBoardFlyout || queryParams?.boardStatusFilter?.length ? 'selected' : ''
                }`}
                onClick={toggleBoardFlyout}
                ref={boardButtonRef}
              >
                <LayoutGrid size={20} className="menu-icon" />
                {label(
                  `Board${
                    queryParams?.boardStatusFilter?.length
                      ? ` (${queryParams.boardStatusFilter.length})`
                      : ''
                  }`
                )}
              </li>
            </ul>
          </>
        )}

        {isOpen && <div className="menu-section-header">Reports</div>}
        <ul className="menu-section">
          <li title="FFC Report" onClick={() => navigate('/ffc-report')}>
            <FileText size={20} className="menu-icon" />
            {label('FFC')}
          </li>
        </ul>
        <ul className="menu-section">
          <li title="AR Report" onClick={() => navigate('/ar-report')}>
            <CircleDollarSign size={20} className="menu-icon" />
            {label('AR Report')}
          </li>
        </ul>
        {(isAdmin || isLeadership) && (
          <>
            {isOpen && <div className="menu-section-header">Leadership</div>}
            <ul className="menu-section">
              {(isAdmin || isLeadership) && (
                <li onClick={() => navigate('/leadership-report')}>
                  <Hospital size={20} />
                  {label('Leadership')}
                </li>
              )}
            </ul>
            {/* <ul className="menu-section">
              {(isAdmin || isLeadership) && (
                <li onClick={() => navigate('/argus-report')}>
                  <Eye size={20} />
                  {label('Argus')}
                </li>
              )}
            </ul>
            <ul className="menu-section">
              {(isAdmin || isLeadership) && (
                <li onClick={() => navigate('/hierarchy-report')}>
                  <ListTree size={20} />
                  {label('Hierarchy')}
                </li>
              )}
            </ul> */}
          </>
        )}
        {(isAdmin) && (
          <>        
            {isOpen && <div className="menu-section-header">Admin</div>}
            <ul className="menu-section">
              {isAdmin && (
                <li onClick={() => navigate('/auth-form')}>
                  <LockKeyholeOpen size={20} />
                  {label('Authorization')}
                </li>
              )}
            </ul>
            <ul className="menu-section">
              {isAdmin && (
                <li onClick={() => navigate('/rules-form')}>
                  <Scroll  size={20} />
                  {label('Rules')}
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      {isVisitsPage && showLabelFlyout && (
        <div
          className="label-flyout"
          ref={flyoutRef}
          style={{ left: isOpen ? 174 : 60, top: 110 }}
        >
          <div className="flyout-title">Select Labels</div>
          <div className="flyout-controls">
            <button onClick={selectAll}>Select All</button>
            <button onClick={deselectAll}>Deselect All</button>
          </div>

          <div className="pills-container vertical">
            {labelList
              .slice()
              .sort((a, b) => a.priority - b.priority)
              .map((labelItem) => {
                const isSelected = labelFilter.includes(labelItem.label_name);
                return (
                  <button
                    key={labelItem.label_name}
                    className={`label-pill ${isSelected ? 'selected' : ''}`}
                    style={{
                      borderColor: labelItem.color,
                      backgroundColor: isSelected
                        ? labelItem.color
                        : 'transparent',
                      color: isSelected ? '#fff' : labelItem.color,
                    }}
                    onClick={() => {
                      if (!setQueryParams) return;
                      const newFilter = isSelected
                        ? labelFilter.filter(
                            (l) => l !== labelItem.label_name
                          )
                        : [...labelFilter, labelItem.label_name];

                      setQueryParams((prev) => ({
                        ...prev,
                        labelFilter: newFilter,
                        page: 1,
                      }));
                    }}
                  >
                    {labelItem.label_name}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {isVisitsPage && showStatusFlyout && (
        <div
          className="label-flyout"
          ref={statusFlyoutRef}
          style={{ left: isOpen ? 174 : 60, top: 260 }}
        >
          <div className="flyout-title">Select Status</div>

          <div className="pills-container vertical">
            {card_status_list.map((status) => {
              const isSelected = queryParams?.statusFilter?.includes(status);

              const key = status.toLowerCase().replace(' ', '-');

              const className = `
                label-pill
                status
                status-${key}-${isSelected ? 'selected' : 'unselected'}
              `;

              return (
                <button
                  key={status}
                  className={className}
                  onClick={() => {
                    if (!setQueryParams) return;

                    const next = isSelected
                      ? queryParams.statusFilter.filter((s) => s !== status)
                      : [...queryParams.statusFilter, status];

                    setQueryParams((prev) => ({
                      ...prev,
                      statusFilter: next,
                      page: 1,
                    }));
                  }}
                >
                  {status}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {isVisitsPage && showBoardFlyout && (
        <div
          className="label-flyout"
          ref={boardFlyoutRef}
          style={{ left: isOpen ? 174 : 60, top: 320 }}
        >
          <div className="flyout-title">Select Board</div>

          <div className="pills-container vertical">
            {boardStatusList.map((status) => {
              const isSelected =
                queryParams?.boardStatusFilter?.includes(status);

              const key = status.toLowerCase().replaceAll(' ', '-');

              return (
                <button
                  key={status}
                  className={`label-pill board board-${key}-${isSelected ? 'selected' : 'unselected'}`}
                  onClick={() => {
                    const next = isSelected
                      ? queryParams.boardStatusFilter.filter(s => s !== status)
                      : [...queryParams.boardStatusFilter, status];

                    setQueryParams(prev => ({
                      ...prev,
                      boardStatusFilter: next,
                      page: 1,
                    }));
                  }}
                >
                  {status}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Menu;